import * as Sentry from '@sentry/react';

import { ApiError } from '../lib/api';
import { config } from '../lib/config';

// Copied from backend/src/types/errors/types.ts
// In the future, we should move this to a shared package
export enum ErrorCode {
  INVALID_REQUEST = 'invalid_request',
  UNAUTHORIZED = 'unauthorized',
  TOKEN_EXPIRED = 'token_expired',
  INVALID_TOKEN = 'invalid_token',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not_found',
  SERVER_ERROR = 'server_error',
  SERVER_OVERLOADED = 'server_overloaded',
  RATE_LIMITED = 'rate_limited',
  VALIDATION_ERROR = 'validation_error',
  NETWORK_ERROR = 'network_error',
  QUOTA_EXCEEDED = 'quota_exceeded',
  CONTENT_FILTERED = 'content_filtered',
  TIMEOUT = 'timeout',
  UNKNOWN_ERROR = 'unknown_error',
  EXTERNAL_SERVICE_ERROR = 'external_service_error',
}

interface ErrorWithCode {
  errorCode: string;
}

interface ErrorWithType {
  type: string;
}

interface ErrorWithStatus {
  status: number;
}

let errorHandler: ErrorHandler | null = null;

interface ErrorHandler {
  (error: Error): void;
}

type Method = 'POST' | 'GET' | 'PUT';

export const setErrorHandler = (handler: ErrorHandler) => {
  errorHandler = handler;
};

// Enhanced error handling with better Sentry integration
export const captureError = (
  error: Error,
  endpoint: string,
  method: Method,
  extraContext: Record<string, unknown> = {}
) => {
  // Call the toast error handler
  if (errorHandler) {
    errorHandler(error);
  }

  // Capture in Sentry with better error formatting
  if (config.useSentry) {
    // Only capture in non-dev environments
    Sentry.withScope((scope) => {
      // Add standard context
      scope.setExtra('endpoint', endpoint);
      scope.setExtra('method', method);
      scope.setExtra('apiUrl', config.apiUrl);

      // Add any additional context
      Object.entries(extraContext).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      if (error instanceof ApiError) {
        scope.setTag('status', error.status);
        scope.setTag('errorType', error.type || 'api_error');
      }

      scope.setTag('endpoint', endpoint);

      scope.setContext('technical', {
        lastOperation: `${method} ${endpoint}`,
        responseBody: extraContext.responseBody,
      });

      // Explicitly capture as error
      Sentry.captureException(error);
    });
  }
};

/**
 * Extracts a standardized error code from any error type
 * @param error The error object from any source
 * @returns A standardized error code string
 */
export function extractErrorCode(error: unknown): string {
  // Default error code
  const errorCode = ErrorCode.UNKNOWN_ERROR;

  // Handle Error instances
  if (error instanceof Error) {
    // Handle ApiError case
    if (error instanceof ApiError && error.type) {
      return error.type;
    }

    // Check for errorCode property
    if ('errorCode' in error && typeof (error as ErrorWithCode).errorCode === 'string') {
      return (error as ErrorWithCode).errorCode;
    }

    // Check for type property
    if ('type' in error && typeof (error as ErrorWithType).type === 'string') {
      return (error as ErrorWithType).type;
    }
  }

  // Handle plain objects
  if (typeof error === 'object' && error !== null) {
    // Check for errorCode
    if ('errorCode' in error && typeof (error as ErrorWithCode).errorCode === 'string') {
      return (error as ErrorWithCode).errorCode;
    }

    // Check for type
    if ('type' in error && typeof (error as ErrorWithType).type === 'string') {
      return (error as ErrorWithType).type;
    }

    // Check for status code
    if ('status' in error) {
      const status = (error as ErrorWithStatus).status;
      if (status === 503) return ErrorCode.SERVER_OVERLOADED;
      if (status === 400) return ErrorCode.INVALID_REQUEST;
      if (status === 401) return ErrorCode.UNAUTHORIZED;
      if (status === 403) return ErrorCode.FORBIDDEN;
      if (status === 429) return ErrorCode.RATE_LIMITED;
    }
  }

  return errorCode;
}
